import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    AccountTypeView,
    ContactView,
    MarketView,
    QuickSearchContactsParameters,
    SearchUsersParameters,
    UserView
} from '@api-proxies';
import { AccountTypeService } from '@http/clients/account-type.service';
import { ContactService } from '@http/clients/contact.service';
import { MarketService } from '@http/clients/market.service';
import { UserService } from '@http/clients/user.service';
import { FilterStateService } from '@services/filter-state.service';
import { AdditionalFiltersComponent } from '@shared/additional-filters/additional-filters.component';
import { ClearFiltersButtonComponent } from '@shared/clear-filters-button/clear-filters-button.component';
import { CustomMenuItemComponent } from '@shared/custom-menu-item/custom-menu-item.component';
import { ITypeAheadService } from '@shared/generic-type-ahead/generic-type-ahead.component';
import { FilterService, IGetById, SearchInputComponent } from 'ngx-lib';
import { Subscription } from 'rxjs';
import { ACCOUNTS_FILTER_CONFIG, ACCOUNTS_FILTER_KEY, IFilterValueAccounts } from './accounts-filter.config';

@Component({
    selector: 'app-accounts-filters',
    standalone: true,
    templateUrl: './accounts-filters.component.html',
    styleUrls: ['./accounts-filters.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SearchInputComponent, ClearFiltersButtonComponent, CustomMenuItemComponent, AdditionalFiltersComponent]
})
export class AccountsFiltersComponent implements OnInit, OnDestroy {
    filtersKey = ACCOUNTS_FILTER_KEY;

    additionalFiltersConfig = ACCOUNTS_FILTER_CONFIG;

    filters: IFilterValueAccounts = {};

    accountTypes: AccountTypeView[] = [];

    markets: MarketView[] = [];

    contactServiceFilter?: ITypeAheadService<ContactView, QuickSearchContactsParameters>;
    userServiceFilter?: ITypeAheadService<UserView, SearchUsersParameters>;

    disabledClearFiltersBtn = false;

    private filterSubscription?: Subscription;

    private readonly contactService = inject(ContactService);
    private readonly accountTypeService = inject(AccountTypeService);
    private readonly marketService = inject(MarketService);
    private readonly filterStateService = inject(FilterStateService<IFilterValueAccounts>);
    private readonly filterService = inject(FilterService);
    private readonly userService = inject(UserService);
    private readonly activatedRoute = inject(ActivatedRoute);

    async ngOnInit(): Promise<void> {
        this.contactServiceFilter = this.contactService;
        this.userServiceFilter = this.userService;
        this.accountTypes = await this.accountTypeService.getAll();
        this.markets = await this.marketService.getAll();

        await this.deserializeUrlParams();

        if (this.filtersKey && Object.keys(this.activatedRoute.snapshot.queryParams).length === 0) {
            const filtersFromLocalStorage = this.filterStateService.getFromLocalStorage(
                this.filtersKey
            ) as IFilterValueAccounts;

            this.filters = {
                ...this.filters,
                ...(filtersFromLocalStorage ? filtersFromLocalStorage : {})
            };
        }

        this.filterStateService.updateFilters(this.filters);

        this.filterSubscription = this.filterStateService.stateFilters$.subscribe(async (filters) => {
            if (!filters) {
                this.filters = {} as IFilterValueAccounts;
            } else {
                this.filters = filters as IFilterValueAccounts;
                await this.updateFilterUrlParams();
                this.disabledClearFiltersBtn = !this.someFilterApplied();
            }
            this.filterStateService.saveToLocalStorage(this.filtersKey, this.filters);
        });
    }

    ngOnDestroy(): void {
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
    }

    onFilterChanged(): void {
        this.filterStateService.updateMainFilters(this.filters);
    }

    private async updateFilterUrlParams(): Promise<void> {
        await this.filterService.serialize<IFilterValueAccounts>(this.filters, 'sortBy');
        await this.filterService.serialize<IFilterValueAccounts>(this.filters, 'sortDirection');
        await this.filterService.serialize<IFilterValueAccounts>(this.filters, 'searchText');
        await this.filterService.serializeArray<IFilterValueAccounts, AccountTypeView>(
            this.filters,
            'accountType',
            'id'
        );
        await this.filterService.serializeArray<IFilterValueAccounts, MarketView>(this.filters, 'market', 'id');
        await this.filterService.serializeBoolean<IFilterValueAccounts>(this.filters, 'includeArchived');
        await this.filterService.serializeTypeAhead<IFilterValueAccounts, UserView>(
            'salespersons',
            this.filters.salespersons,
            'externalId',
            true
        );
    }

    private async deserializeUrlParams() {
        this.filterService.deserialize<IFilterValueAccounts>(this.filters, 'sortBy');
        this.filterService.deserialize<IFilterValueAccounts>(this.filters, 'sortDirection');
        this.filterService.deserialize<IFilterValueAccounts>(this.filters, 'searchText');
        this.filterService.deserializeArray<IFilterValueAccounts, AccountTypeView>(
            this.filters,
            'accountType',
            'id',
            this.accountTypes ?? []
        );
        this.filterService.deserializeArray<IFilterValueAccounts, MarketView>(
            this.filters,
            'market',
            'id',
            this.markets ?? []
        );
        this.filterService.deserializeBoolean<IFilterValueAccounts>(this.filters, 'includeArchived');
        await this.filterService.deserializeTypeAhead<IFilterValueAccounts, UserView>(
            this.filters,
            'salespersons',
            this.userService as IGetById,
            true
        );
    }

    private someFilterApplied(): boolean {
        return Object.values(this.filters).some((f) =>
            Array.isArray(f) ? f.length > 0 : f !== null && f !== undefined && f !== ''
        );
    }
}
